import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Step,
  StepLabel,
  Stepper,
  Card,
  Chip,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Gavel,
  Business,
  FamilyRestroom,
  HomeWork,
  Work,
  Description,
  MonetizationOn,
  CleaningServices,
  Security,
  Fingerprint,
  CardTravel,
  Public,
  TrendingUp
} from '@mui/icons-material';
import { requestService } from '../services/api';

const serviceTypes = [
  // Services juridiques existants
  { value: 'consultation', label: 'Consultation juridique', icon: <Gavel />, category: 'Juridique' },
  { value: 'recouvrement', label: 'Recouvrement de créances', icon: <MonetizationOn />, category: 'Juridique' },
  { value: 'immobilier', label: 'Services immobiliers', icon: <HomeWork />, category: 'Immobilier' },
  { value: 'formalities', label: 'Formalités administratives', icon: <Description />, category: 'Administratif' },
  
  // Nouveaux services
  { value: 'montage_dao', label: 'Montage de dossiers d\'appel d\'offres (DAO)', icon: <Assignment />, category: 'Marchés Publics' },
  { value: 'marches_publics', label: 'Consultant en marchés publics', icon: <TrendingUp />, category: 'Marchés Publics' },
  { value: 'nettoyage', label: 'Nettoyage et entretien (bureaux/appartements)', icon: <CleaningServices />, category: 'Prestations' },
  { value: 'fournitures_entretien', label: 'Fournitures de produits d\'entretien', icon: <CleaningServices />, category: 'Prestations' },
  { value: 'securite_gardiennage', label: 'Sécurité et gardiennage', icon: <Security />, category: 'Prestations' },
  { value: 'casier_judiciaire', label: 'Demande de casier judiciaire', icon: <Fingerprint />, category: 'Administratif' },
  { value: 'passeport', label: 'Demande de passeport', icon: <CardTravel />, category: 'Administratif' },
  { value: 'nationalite', label: 'Démarches de nationalité', icon: <Public />, category: 'Administratif' }
];

const legalDomains = [
  { value: 'family', label: 'Droit de la famille', icon: <FamilyRestroom /> },
  { value: 'work', label: 'Droit du travail', icon: <Work /> },
  { value: 'property', label: 'Droit immobilier', icon: <HomeWork /> },
  { value: 'business', label: 'Droit des affaires', icon: <Business /> },
  { value: 'civil', label: 'Droit civil', icon: <Gavel /> },
  { value: 'other', label: 'Autre domaine', icon: <Description /> },
];

const steps = ['Informations personnelles', 'Détails de la demande', 'Confirmation'];

const ConsultationForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceType: '',
    legalDomain: '',
    description: '',
    subject: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await requestService.submitRequest(formData);
      setSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        serviceType: '',
        legalDomain: '',
        description: '',
        subject: ''
      });
      setActiveStep(0);
      
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    if (activeStep === 0) {
      return formData.fullName && formData.email && formData.phone;
    }
    if (activeStep === 1) {
      return formData.serviceType && formData.description;
    }
    return true;
  };

  const getSelectedServiceLabel = () => {
    const service = serviceTypes.find(s => s.value === formData.serviceType);
    return service ? service.label : 'Non spécifié';
  };

  const getServiceCategory = () => {
    const service = serviceTypes.find(s => s.value === formData.serviceType);
    return service ? service.category : '';
  };

  return (
    <Container maxWidth="md">
      <Paper 
        elevation={0} 
        className="fade-in-up"
        sx={{ 
          p: { xs: 3, md: 4 }, 
          mt: 4, 
          mb: 4,
          borderRadius: 4,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar sx={{ bgcolor: '#1a237e', width: 64, height: 64, margin: '0 auto', mb: 2 }}>
            <Assignment sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ color: '#1a237e' }}>
            Demande de Service
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Remplissez ce formulaire pour toute demande de service. 
            Nos experts vous répondront dans les plus brefs délais.
          </Typography>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} icon={<CheckCircle />}>
            ✅ Votre demande a été envoyée avec succès. Nous vous contacterons rapidement.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            ❌ {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label="Nom complet"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  label="Téléphone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="+228 XX XX XX XX"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Type de service</InputLabel>
                  <Select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    label="Type de service"
                  >
                    {serviceTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ mr: 2, color: '#1a237e' }}>{option.icon}</Box>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Chip 
                              label={option.category} 
                              size="small" 
                              sx={{ 
                                fontSize: '0.7rem', 
                                height: 20,
                                backgroundColor: '#ff9800',
                                color: '#1a237e'
                              }} 
                            />
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Sélectionnez le service qui vous intéresse</FormHelperText>
                </FormControl>
              </Grid>

              {formData.serviceType === 'consultation' && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Domaine juridique (optionnel)</InputLabel>
                    <Select
                      name="legalDomain"
                      value={formData.legalDomain}
                      onChange={handleChange}
                      label="Domaine juridique (optionnel)"
                    >
                      {legalDomains.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ mr: 1 }}>{option.icon}</Box>
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={6}
                  label="Description de votre demande"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez en détail votre besoin ou votre situation..."
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 2 && (
            <Card elevation={0} sx={{ p: 3, bgcolor: '#f5f7fa', borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#1a237e' }}>
                Résumé de votre demande
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography><strong>Nom :</strong> {formData.fullName}</Typography>
                <Typography><strong>Email :</strong> {formData.email}</Typography>
                <Typography><strong>Téléphone :</strong> {formData.phone}</Typography>
                <Typography>
                  <strong>Service :</strong> {getSelectedServiceLabel()}
                  {getServiceCategory() && (
                    <Chip 
                      label={getServiceCategory()} 
                      size="small" 
                      sx={{ ml: 1, backgroundColor: '#ff9800', color: '#1a237e' }} 
                    />
                  )}
                </Typography>
                {formData.legalDomain && (
                  <Typography>
                    <strong>Domaine juridique :</strong> {legalDomains.find(d => d.value === formData.legalDomain)?.label || 'Non spécifié'}
                  </Typography>
                )}
                <Typography sx={{ mt: 2 }}>
                  <strong>Description :</strong> {formData.description.substring(0, 150)}...
                </Typography>
              </Box>
            </Card>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ borderRadius: 50, px: 4 }}
            >
              Retour
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ borderRadius: 50, px: 4 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Envoyer la demande'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid()}
                sx={{ borderRadius: 50, px: 4 }}
              >
                Suivant
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Informations supplémentaires */}
      <Paper sx={{ p: 3, mt: 2, borderRadius: 3, bgcolor: '#fff3e0' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          <strong>📞 Besoin d'une réponse immédiate ?</strong><br />
          Appelez-nous au +228 90 35 87 66 / 99 54 90 41 ou écrivez à aujaagroup14@gmail.com
        </Typography>
      </Paper>
    </Container>
  );
};

export default ConsultationForm;